import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AnimatedHeading from "./AnimatedHeading";
import PrimaryCTA from "./PrimaryCTA";

const PHONE_RE = /^[+0-9 ()\-./]{6,32}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ContactForm() {
    const [form, setForm] = useState({ nome: "", azienda: "", telefono: "", email: "", messaggio: "", website: "" });
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle"); // idle | sending | success | error
    const [errMsg, setErrMsg] = useState("");

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const validate = () => {
        const e = {};
        if (form.nome.trim().length < 2) e.nome = "Inserisci nome e cognome";
        if (form.azienda.trim().length < 2) e.azienda = "Inserisci nome azienda o progetto";
        if (!PHONE_RE.test(form.telefono.trim())) e.telefono = "Numero di telefono non valido";
        if (!EMAIL_RE.test(form.email.trim())) e.email = "Email non valida";
        if (form.messaggio.length > 500) e.messaggio = "Messaggio troppo lungo (max 500)";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onSubmit = async (ev) => {
        ev.preventDefault();
        if (!validate()) return;
        setStatus("sending");
        setErrMsg("");
        try {
            const backend = process.env.REACT_APP_BACKEND_URL || "";
            const payload = { ...form };
            if (!payload.messaggio) delete payload.messaggio;
            const resp = await fetch(`${backend}/api/public/contact`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (resp.status === 429) {
                setStatus("error");
                setErrMsg("Troppe richieste. Riprova tra un'ora.");
                return;
            }
            if (!resp.ok) {
                setStatus("error");
                setErrMsg("Qualcosa non è andato. Riprova.");
                return;
            }
            setStatus("success");
        } catch {
            setStatus("error");
            setErrMsg("Errore di rete. Riprova.");
        }
    };

    return (
        <section className="lp-section" aria-labelledby="s-contact" id="contatti">
            <div className="lp-container">
                <div className="lp-section-head">
                    <motion.div
                        className="lp-eyebrow lp-section-eyebrow"
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <span className="lp-eyebrow-accent">→</span> &nbsp;/&nbsp; Contatti
                    </motion.div>
                    <AnimatedHeading
                        as="h2"
                        id="s-contact"
                        className="lp-h2"
                        text="Lasciaci un contatto. Ti richiamiamo noi."
                    />
                    <motion.p
                        className="lp-body-lg lp-section-sub"
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
                    >
                        Ti ricontattiamo entro 24h.
                    </motion.p>
                </div>

                <motion.div
                    className="lp-form-card"
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                >
                    <AnimatePresence mode="wait">
                        {status === "success" ? (
                            <motion.div
                                key="success"
                                className="lp-success"
                                data-testid="contact-success"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.6 }}
                            >
                                <div className="lp-success-check" aria-hidden="true">
                                    <svg viewBox="0 0 32 32">
                                        <path d="M6 16 L14 24 L26 8" />
                                    </svg>
                                </div>
                                <h3>Grazie, ti ricontattiamo entro 24h.</h3>
                                <p>Se non ci senti entro 1 giorno lavorativo, scrivi a info@enerleft.it</p>
                            </motion.div>
                        ) : (
                            <motion.form
                                key="form"
                                onSubmit={onSubmit}
                                noValidate
                                data-testid="contact-form"
                                initial={{ opacity: 1 }}
                                exit={{ opacity: 0, y: -12 }}
                                transition={{ duration: 0.3 }}
                            >
                                <div className="lp-form-grid">
                                    <div className="lp-field">
                                        <label htmlFor="c-nome">Nome e cognome</label>
                                        <input id="c-nome" className="lp-input" type="text"
                                            value={form.nome} onChange={(e) => setField("nome", e.target.value)}
                                            aria-invalid={!!errors.nome} data-testid="contact-nome" required />
                                        {errors.nome && <div className="lp-field-error">{errors.nome}</div>}
                                    </div>
                                    <div className="lp-field">
                                        <label htmlFor="c-azienda">Azienda o progetto</label>
                                        <input id="c-azienda" className="lp-input" type="text"
                                            value={form.azienda} onChange={(e) => setField("azienda", e.target.value)}
                                            aria-invalid={!!errors.azienda} data-testid="contact-azienda" required />
                                        {errors.azienda && <div className="lp-field-error">{errors.azienda}</div>}
                                    </div>
                                    <div className="lp-field">
                                        <label htmlFor="c-tel">Telefono</label>
                                        <input id="c-tel" className="lp-input" type="tel"
                                            value={form.telefono} onChange={(e) => setField("telefono", e.target.value)}
                                            aria-invalid={!!errors.telefono} data-testid="contact-telefono" required />
                                        {errors.telefono && <div className="lp-field-error">{errors.telefono}</div>}
                                    </div>
                                    <div className="lp-field">
                                        <label htmlFor="c-email">Email</label>
                                        <input id="c-email" className="lp-input" type="email"
                                            value={form.email} onChange={(e) => setField("email", e.target.value)}
                                            aria-invalid={!!errors.email} data-testid="contact-email" required />
                                        {errors.email && <div className="lp-field-error">{errors.email}</div>}
                                    </div>
                                    <div className="lp-field lp-form-row-full">
                                        <label htmlFor="c-msg">Messaggio (opzionale)</label>
                                        <textarea id="c-msg" className="lp-textarea" maxLength={500}
                                            value={form.messaggio} onChange={(e) => setField("messaggio", e.target.value)}
                                            aria-invalid={!!errors.messaggio} data-testid="contact-messaggio" />
                                        {errors.messaggio && <div className="lp-field-error">{errors.messaggio}</div>}
                                    </div>
                                </div>
                                {/* Honeypot */}
                                <div className="lp-honeypot" aria-hidden="true">
                                    <label htmlFor="c-website">Website</label>
                                    <input id="c-website" name="website" type="text" tabIndex={-1} autoComplete="off"
                                        value={form.website} onChange={(e) => setField("website", e.target.value)} />
                                </div>
                                {status === "error" && <div className="lp-form-error" data-testid="contact-error">{errMsg}</div>}
                                <div className="lp-form-actions">
                                    <span className="lp-form-note">Risposta entro 24h · nessuno spam</span>
                                    <PrimaryCTA type="submit" disabled={status === "sending"} dataTestId="contact-submit">
                                        {status === "sending" ? "Invio in corso…" : "Fatti richiamare"}
                                    </PrimaryCTA>
                                </div>
                            </motion.form>
                        )}
                    </AnimatePresence>
                </motion.div>
            </div>
        </section>
    );
}
